import React from 'react';
import { Sparkles, BookOpen, Crown } from 'lucide-react';

/**
 * Three-button reading-level selector used by every upload form.
 *
 * Stored on the same `ai_reading_level` field the existing reader-side
 * filter chips already key off, so picking a level here makes the
 * uploaded item show up under the matching Library filter without any
 * additional plumbing.
 *
 * Optional — if the uploader leaves it blank, the AI auto-classifier
 * still runs later and fills the field in.
 */
const LEVELS = [
  {
    id: 'Beginner',
    label: 'Beginner',
    helper: 'Easy reads, gentle pace',
    Icon: BookOpen,
    accent: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
    inactive: 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-emerald-500/40',
  },
  {
    id: 'Intermediate',
    label: 'Intermediate',
    helper: 'Moderate complexity',
    Icon: Sparkles,
    accent: 'border-amber-500/60 bg-amber-500/10 text-amber-200',
    inactive: 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-amber-500/40',
  },
  {
    id: 'Advanced',
    label: 'Advanced',
    helper: 'Dense, challenging',
    Icon: Crown,
    accent: 'border-rose-500/60 bg-rose-500/10 text-rose-200',
    inactive: 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-rose-500/40',
  },
];

const ReadingLevelSelect = ({ value, onChange, label = 'Reading level', optional = true }) => {
  return (
    <div data-testid="reading-level-select">
      <label className="block text-sm font-medium text-slate-200 mb-1">
        {label}{' '}
        {optional && (
          <span className="text-xs text-slate-500 font-normal">(optional)</span>
        )}
      </label>
      <p className="text-xs text-slate-500 mb-2">
        Tag this upload so readers can filter by difficulty. Leave blank to let
        the AI classifier decide.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {LEVELS.map((lvl) => {
          const active = value === lvl.id;
          const Icon = lvl.Icon;
          return (
            <button
              key={lvl.id}
              type="button"
              onClick={() => onChange(active ? '' : lvl.id)}
              data-testid={`reading-level-${lvl.id.toLowerCase()}`}
              className={`relative rounded-lg border px-3 py-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${
                active ? lvl.accent : lvl.inactive
              }`}
            >
              <div className="flex items-center gap-1.5 font-semibold text-sm">
                <Icon className="w-3.5 h-3.5" /> {lvl.label}
              </div>
              <div className="text-[10px] mt-0.5 opacity-80">{lvl.helper}</div>
              {active && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-current" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReadingLevelSelect;
